import { field } from '../fields'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, AlertTriangle, Check, ChevronDown, ChevronRight, Copy, KeyRound, Link2, Mail, MessageSquare, Search, ShieldCheck, Sparkles, UserPlus, Users, X } from 'lucide-react'
import { fetchContent, text } from '../api/content'
import {
  addStaff,
  byGroup,
  changeRole,
  fetchCapabilities,
  fetchStaffAccounts,
  filterAccounts,
  grant,
  humanise,
  dancerCandidates,
  ExistingLogin,
  invite,
  linkDancer,
  linkExistingLogin,
  linkFamily,
  monthAndYear,
  renameStaff,
  setStaffContact,
  restore,
  retireStaff,
  revoke,
  ROLES,
  STATE_LABELS,
  stillLockedOut,
  searchGuardians,
  suspend,
  unlinkDancer,
  unlinkFamily,
  unretireStaff,
  type AccountState,
  type DancerOption,
  type CapabilityInfo,
  type StaffAccount,
  type UserRole,
} from '../api/staffAdmin'
import { useCan } from '../useSession'
import ConfirmAction from './ConfirmAction'

const STATE_STYLES: Record<AccountState, string> = {
  None: 'bg-surface-2 text-ink-3',
  Invited: 'bg-warn-bg text-warn',
  InviteExpired: 'bg-crit-bg text-crit',
  Active: 'bg-good-bg text-good',
  Suspended: 'bg-crit-bg text-crit',
}

/**
 * Who can sign in, as what, and with which extra permissions.
 *
 * Ordered by the question it answers: **who still cannot get in?** The import
 * brings 48 staff and zero logins, so the screen leads with that count and the
 * people behind it, rather than presenting a neutral table where the gap has to
 * be counted by eye.
 */
export default function StaffAdminView() {
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  /*
   * <b>A search hit reveals her row; it does not expand it</b> (D-463).
   *
   * The obvious build puts `expanded` in the URL, the way the class panel now
   * does. It would be a security bug. `setExpanded` is only reachable behind
   * `useCan('GrantPermissions')`, and what it opens is the **permissions
   * editor** — so moving it into the address removes the one check doing that
   * work, and any `ManageStaff` holder following a search hit would land on a
   * grid of every capability in the system with live-looking buttons that all
   * return 403. Admin holds `ManageStaff` and not `GrantPermissions`, so that
   * reader exists in the role defaults rather than in theory.
   *
   * Found by a cold red team, against a spec scenario that asked for exactly
   * the wrong thing.
   *
   * Revealing is also what "opens what you clicked" means here: this row is
   * already the whole record — name, email, title, state, role, invite state.
   * The only thing expanding adds is the editor nobody searching for Hannah
   * Jones asked for.
   */
  const [params] = useSearchParams()
  const revealed = params.get('staff')
  const revealedRow = useRef<HTMLLIElement | null>(null)

  /*
   * <b>And clearing this screen's own filter, which would otherwise hide her.</b>
   *
   * `query` is component state and survives a same-route navigate: type
   * "smith" here, then search "Jones" in the header, and the list still shows
   * Smiths with Hannah Jones absent and nothing saying why. Arriving with
   * somebody named is a new question, so the old one is dropped.
   */
  useEffect(() => {
    if (revealed) setQuery('')
  }, [revealed])
  const [linking, setLinking] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState<{ name: string; link: string } | null>(null)
  const [adding, setAdding] = useState(false)

  /** What the last departure actually did to her login (D-605). */
  const [departed, setDeparted] = useState<string | null>(null)

  /** Whether the people who no longer work here are on screen (D-605). */
  const [showFormer, setShowFormer] = useState(false)

  /** Which row is showing its dancer picker (D-604). */
  const [linkingDancer, setLinkingDancer] = useState<string | null>(null)

  /**
   * An invitation that could not happen because she already signs in (D-604).
   *
   * Held on the screen rather than acted on: attaching a staff record to an
   * account somebody else created is Tonya saying two records are one person,
   * and doing it on an email match is the account-takeover path D-535 refused.
   */
  const [offerLink, setOfferLink] = useState<{
    staffId: string
    staffName: string
    accountId: string
    accountName: string
  } | null>(null)

  const canGrant = useCan('GrantPermissions')

  // The supervisory view (D-626): owner-only by default, and it survives the
  // module switch, so it is gated on the capability and never on `modules`.
  const canReview = useCan('ReviewConversations')
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery({
    queryKey: ['staff-accounts'],
    queryFn: fetchStaffAccounts,
  })

  const { data: capabilities } = useQuery({
    queryKey: ['capabilities'],
    queryFn: fetchCapabilities,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['staff-accounts'] })

  /** Every action here shares one shape: do it, refresh, or show why not. */
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  })

  const rows = useMemo(() => filterAccounts(data ?? [], query), [data, query])

  /*
   * <b>Bringing her into view, because nothing else will</b> (D-463).
   *
   * `?staff=` is a query parameter, not a fragment, so the browser does not
   * jump to the matching `id` and `scroll-mt-*` alone is inert. There are 48
   * staff on one unpaginated list ordered by first name, so without this a
   * reader following a search hit lands at the top of exactly the list this
   * change exists to stop — with the highlight somewhere below the fold.
   *
   * Keyed on `revealed` rather than running once, so a second search while
   * already on this screen moves to the new row.
   */
  useEffect(() => {
    if (!revealed) return
    revealedRow.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [revealed, rows])

  const lockedOut = data ? stillLockedOut(data) : 0

  /*
   * <b>Former staff fold out of the way</b> (D-605). `asked`: they were
   * "muddying up the whole page". They are kept, never deleted — a class taught
   * in October still names whoever taught it — so the answer is where they sit
   * rather than whether they exist.
   *
   * <b>A search shows everybody.</b> Typing a name is asking about that person,
   * and a screen that answers "no such teacher" because she left in March is
   * worse than a long list: it reads as data missing rather than data filed.
   */
  const searching = query.trim().length > 0

  /*
   * <b>And whoever the address named stays on screen</b> (D-463). Arriving at
   * `?staff=` clears this screen's own search box, so a former member would
   * fold away and the reveal would scroll to a row that is not there — the
   * exact failure D-463's comment describes, one cause over.
   */
  const kept = (r: StaffAccount) => r.staffIsActive || r.staffId === revealed
  const current = searching ? rows : rows.filter(kept)
  const former = searching ? [] : rows.filter((r) => !kept(r))
  const shown = showFormer ? [...current, ...former] : current

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Admin</p>
        <h1 className="font-display text-2xl">Staff &amp; access</h1>
        <p className="mt-1.5 max-w-[44rem] text-[13.5px] text-ink-3">
          Everyone imported from Jackrabbit is here, and you can add somebody who is not.
          Invite them to create a login, and give individuals extra permissions without
          promoting them.
        </p>
      </header>

      {/*
        **Adding a person is not the same act as giving her a login**, so it
        sits beside the list rather than inside the invite flow. Until this
        existed every teacher arrived by import, and somebody hired in
        September could not be entered until the next export (D-534).
      */}
      {adding ? (
        <AddTeacher
          onClose={() => setAdding(false)}
          onAdded={(name, staffId) => {
            setAdding(false)
            setError(null)
            refresh()

            // Filter to her, because the list is 48 people ordered by first
            // name and the one just added is otherwise somewhere below the
            // fold — the same reason D-463 exists for a search hit.
            setQuery(name)

            /*
             * <b>And open the dancer picker on her new row</b> (D-604).
             *
             * "Is this new person a dancer here?" is asked at exactly this
             * moment, and a student teacher is the commonest reason to be
             * adding somebody by hand at all. A second picker on the form
             * would be a second implementation of one that already exists
             * (A-17), and it could not offer the name hint — that is computed
             * against a staff record which, on the form, does not exist yet.
             * Opening the real one costs no clicks and asks the question
             * where it can be answered well.
             */
            setLinkingDancer(staffId)
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mb-4 flex items-center gap-1.5 rounded-lg border border-line px-3.5 py-2 text-[13px] font-semibold"
        >
          <UserPlus size={14} strokeWidth={2} />
          Add someone
        </button>
      )}

      {lockedOut > 0 && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-warn bg-warn-bg px-4 py-3.5">
          <AlertTriangle size={18} strokeWidth={2.5} className="mt-0.5 shrink-0 text-warn" />
          <div>
            <p className="text-[14px] font-semibold">
              {lockedOut} {lockedOut === 1 ? 'person' : 'people'} still cannot sign in
            </p>
            <p className="mt-0.5 text-[13px] text-ink-2">
              They were never invited, or their invitation ran out.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" className="mb-4 flex items-start gap-3 rounded-xl border border-crit bg-crit-bg px-4 py-3">
          <X size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-crit" />
          <p className="text-[13px] text-crit">{error}</p>
        </div>
      )}

      {/*
        What retiring somebody actually did to her login, which varies and
        which the row shows none of (D-605). Dismissible rather than timed:
        it is the only place the difference is stated.
      */}
      {departed && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-line bg-surface-2 px-4 py-3">
          <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-good" />
          <p className="min-w-0 flex-1 text-[13px]">{departed}</p>
          <button
            type="button"
            onClick={() => setDeparted(null)}
            aria-label="Dismiss"
            className="grid size-8 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface hover:text-ink"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/*
        The invite link is shown rather than emailed, because mail delivery does
        not exist yet. It is a working credential, so it appears once and is not
        stored anywhere the screen can re-read it.
      */}
      {inviteLink && (
        <div className="mb-4 rounded-xl border border-good bg-good-bg px-4 py-3.5">
          <p className="text-[13.5px] font-semibold">Invitation ready for {inviteLink.name}</p>
          <p className="mt-0.5 text-[12.5px] text-ink-2">
            Email is not connected yet, so send this link yourself. It is shown once.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg bg-surface px-2.5 py-1.5 text-[12px]">
              {inviteLink.link}
            </code>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(window.location.origin + inviteLink.link)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[12.5px] font-semibold hover:border-accent"
            >
              <Copy size={13} strokeWidth={2} />
              Copy
            </button>
            <button
              type="button"
              onClick={() => setInviteLink(null)}
              aria-label="Dismiss"
              className="grid size-8 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface-2 hover:text-ink"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}

      <label className="relative mb-4 block">
        <Search size={15} strokeWidth={2} className="absolute top-1/2 left-3 -translate-y-1/2 text-ink-3" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, role, or status..."
          className="w-full rounded-xl border border-line-2 bg-surface py-2.5 pr-3 pl-9 text-[13.5px] outline-none focus:border-accent"
        />
      </label>

      {isPending && <p className="text-sm text-ink-3">Loading staff...</p>}
      {isError && <p role="alert" className="text-sm text-crit">Could not load the staff list.</p>}

      {data && (
        <div className="overflow-hidden rounded-2xl border border-line bg-surface">
          <ul className="divide-y divide-line">
            {shown.map((account) => (
              <li
                key={account.staffId}
                // Marked, not opened. Scrolled to by the effect above,
                // because a query parameter is not a fragment and the browser
                // will not jump to it on its own.
                id={`staff-${account.staffId}`}
                ref={revealed === account.staffId ? revealedRow : undefined}
                className={
                  revealed === account.staffId
                    ? 'scroll-mt-20 border-l-4 border-accent bg-accent-soft'
                    : undefined
                }
              >
                <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                  {/*
                    `basis-[11rem]`, and without it this row is broken on a
                    phone — measured, not guessed (D-310).

                    Every other child here is `shrink-0`, and this one was
                    `flex-1` with the default `basis-0`. A zero basis means the
                    line always "fits" by construction, so `flex-wrap` never
                    wraps it — and the shortfall is then taken entirely out of
                    the only child allowed to shrink. The name rendered
                    **19px wide**, one character per line, in a 470px-tall row.

                    A real basis makes the line genuinely overflow, so it wraps
                    the way it was always meant to: 19px becomes 218px and the
                    row becomes 123px tall.
                  */}
                  <div className="min-w-0 flex-1 basis-[11rem]">
                    <p className="text-[13.5px] font-semibold wrap-anywhere">
                      {account.name}
                      {!account.staffIsActive && (
                        <span className="ml-2 text-[11.5px] font-normal text-ink-3">
                          {account.endedOn ? `left ${monthAndYear(account.endedOn)}` : 'no longer on staff'}
                        </span>
                      )}
                    </p>
                    <p className="text-[12px] wrap-anywhere text-ink-3">
                      {account.email ?? 'No email on file'}
                      {account.title && ` · ${account.title}`}
                    </p>
                  </div>

                  <span
                    className={`shrink-0 rounded-md px-2 py-0.5 text-[11.5px] font-semibold ${STATE_STYLES[account.state]}`}
                  >
                    {STATE_LABELS[account.state]}
                  </span>

                  {account.accountId ? (
                    <RoleControl
                      account={account}
                      onChange={(role) => act.mutate(() => changeRole(account.accountId!, role))}
                      disabled={act.isPending}
                    />
                  ) : (
                    <span className="w-[6.5rem] shrink-0" />
                  )}

                  {/*
                    A minimum width, because the Invite button is conditional.
                    A row with no email on file has no button, so the state
                    chip and the role control slid right on exactly those rows
                    and the column stopped being a column - QA saw *Never
                    invited* wandering. Wide enough for *Re-invite* beside
                    *Name*, right-aligned so a row with only *Name* keeps its
                    button under the others'. A minimum rather than a width:
                    a row with a login carries *Also a parent* and *Suspend*
                    here too, and a fixed width folded those over the role
                    control - measured, the first version did exactly that.
                  */}
                  <div className="flex min-w-[10.75rem] shrink-0 items-center justify-end gap-1.5">
                    {(account.state === 'None' || account.state === 'InviteExpired') && account.email && (
                      <button
                        type="button"
                        disabled={act.isPending}
                        onClick={() => {
                          setError(null)
                          invite({
                            email: account.email!,
                            staffId: account.staffId,
                            role: account.role ?? 'Teacher',
                          })
                            .then((result) => {
                              // Only when nothing actually delivered it. Once
                              // mail is sending, showing the link invites
                              // someone to hand-deliver what is already in an
                              // inbox — and to paste a live credential into a
                              // chat window.
                              if (!result.emailed && result.inviteLink)
                                setInviteLink({ name: account.name, link: result.inviteLink })

                              if (result.emailProblem) setError(result.emailProblem)
                              refresh()
                            })
                            .catch((e: Error) => {
                              /*
                                <b>A refusal with a way out of it</b> (D-604).
                                She signed herself up as a parent, so there is
                                nothing to invite — and reporting only "that
                                address already has an active login" sent Tonya
                                to a password reset that would not have helped.
                                Offered, never taken automatically: the link
                                hands teaching capabilities to an account
                                somebody else made.
                              */
                              if (e instanceof ExistingLogin) {
                                setOfferLink({
                                  staffId: account.staffId,
                                  staffName: account.name,
                                  accountId: e.accountId,
                                  accountName: e.accountName,
                                })
                                return
                              }

                              setError(e.message)
                            })
                        }}
                        className="flex items-center gap-1.5 rounded-lg bg-accent px-2.5 py-1.5 text-[12.5px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-50"
                      >
                        <Mail size={13} strokeWidth={2.5} />
                        {account.state === 'InviteExpired' ? 'Re-invite' : 'Invite'}
                      </button>
                    )}

                    {/*
                      **Correcting a name, which nothing could do** (D-563).
                      Offered on every row rather than only on ones with a
                      login: the name is the staff record's, and the rows most
                      likely to carry a wrong one - a placeholder from the
                      owner bootstrap, a person promoted from a dancer - are
                      exactly the ones that may not have signed in yet.
                    */}
                    <button
                      type="button"
                      onClick={() =>
                        setRenaming(renaming === account.staffId ? null : account.staffId)
                      }
                      className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold ${
                        renaming === account.staffId
                          ? 'border-accent text-accent-2'
                          : 'border-line-2 text-ink-2 hover:border-ink-3'
                      }`}
                    >
                      <Pencil size={13} strokeWidth={2.5} />
                      Details
                    </button>

                    {account.accountId && canGrant && (
                      <button
                        type="button"
                        onClick={() =>
                          setExpanded(expanded === account.staffId ? null : account.staffId)
                        }
                        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold ${
                          expanded === account.staffId
                            ? 'border-accent text-accent-2'
                            : 'border-line-2 text-ink-2 hover:border-ink-3'
                        }`}
                      >
                        <KeyRound size={13} strokeWidth={2.5} />
                        {account.grantedCapabilities.length > 0
                          ? `+${account.grantedCapabilities.length}`
                          : 'Permissions'}
                      </button>
                    )}

                    {/*
                      Offered on every row with a login, and marked when the
                      row has something to say - either a link already made or
                      a suggestion worth confirming. Sixteen people here are
                      both staff and a parent, so this is not a rare case
                      hidden behind a menu (D-305).
                    */}
                    {account.accountId && (
                      <button
                        type="button"
                        onClick={() =>
                          setLinking(linking === account.staffId ? null : account.staffId)
                        }
                        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold ${
                          linking === account.staffId
                            ? 'border-accent text-accent-2'
                            : account.guardianId
                              ? 'border-good text-good'
                              : account.suggestedGuardianId
                                ? 'border-warn text-warn'
                                : 'border-line-2 text-ink-2 hover:border-ink-3'
                        }`}
                      >
                        <Users size={13} strokeWidth={2.5} />
                        {account.guardianId ? 'Also a parent' : 'Link family'}
                      </button>
                    )}

                    {/*
                      <b>On every row, including the ones with no login</b>
                      (D-604), which is the whole difference from the family
                      control above. That one lives on the account, so it needs
                      one; this is person to person on the staff record, and 43
                      of the 48 staff have no account at all — gating it the
                      same way would hide it from almost everybody it is for.

                      <b>And the page held this and never said it.</b> Ava is a
                      dancer here and a member of staff, both from the import,
                      and until now nothing on this screen showed the link.
                    */}
                    <button
                      type="button"
                      onClick={() =>
                        setLinkingDancer(linkingDancer === account.staffId ? null : account.staffId)
                      }
                      className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold ${
                        linkingDancer === account.staffId
                          ? 'border-accent text-accent-2'
                          : account.dancerId
                            ? 'border-good text-good'
                            : 'border-line-2 text-ink-2 hover:border-ink-3'
                      }`}
                    >
                      <Sparkles size={13} strokeWidth={2.5} />
                      {account.dancerId ? 'Also a dancer' : 'Link dancer'}
                    </button>

                    {/*
                      Every conversation this account is part of, read from
                      outside (D-626). Only where there is an account to be in
                      one: 43 of the 48 staff have no login, and a link that
                      opens onto a refusal is worse than none.
                    */}
                    {canReview && account.accountId && (
                      <Link
                        to={`/admin/staff/${account.staffId}/conversations`}
                        className="flex items-center gap-1.5 rounded-lg border border-line-2 px-2.5 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
                      >
                        <MessageSquare size={13} strokeWidth={2.5} />
                        {text(content, 'messages.review.heading', 'Conversations')}
                      </Link>
                    )}

                    {account.accountId && (
                      <button
                        type="button"
                        disabled={act.isPending}
                        onClick={() =>
                          act.mutate(() =>
                            account.state === 'Suspended'
                              ? restore(account.accountId!)
                              : suspend(account.accountId!),
                          )
                        }
                        className="rounded-lg border border-line-2 px-2.5 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:border-crit hover:text-crit disabled:opacity-50"
                      >
                        {account.state === 'Suspended' ? 'Restore' : 'Suspend'}
                      </button>
                    )}

                    {/*
                      <b>She left</b> (D-605). On every row, because 43 of the
                      48 staff have no login and a teacher who leaves is far
                      more often one of those than one of the five.

                      <b>Not the same control as Suspend beside it.</b> That
                      one is about a login and is reversible in a moment — a
                      password worry, somebody on leave. This is about the
                      person, and it takes her out of the cover list, the ask
                      recipients, the search, the PIN pad and the class
                      instructor lists in one act. Suspending her login is
                      part of what it does rather than a second thing to
                      remember.
                    */}
                    <button
                      type="button"
                      disabled={act.isPending}
                      onClick={() => {
                        if (!account.staffIsActive) {
                          act.mutate(() => unretireStaff(account.staffId))
                          return
                        }

                        /*
                          <b>Said, because what happens to her login varies and
                          the row shows none of it</b> (D-605). A teacher with
                          no account loses nothing; a staff-only login is
                          suspended; a teacher who is also a parent keeps hers
                          and loses only the teaching. Folding her away in
                          silence would leave all three looking identical.
                        */
                        act.mutate(async () => {
                          const what = await retireStaff(account.staffId)

                          setDeparted(
                            what.suspendedALogin
                              ? `${account.name} is off the staff list, and her login is suspended.`
                              : what.keptALogin
                                ? `${account.name} is off the staff list. She keeps her login for her family and can no longer take attendance.`
                                : `${account.name} is off the staff list.`,
                          )
                        })
                      }}
                      className="rounded-lg border border-line-2 px-2.5 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:border-crit hover:text-crit disabled:opacity-50"
                    >
                      {account.staffIsActive ? 'She left' : 'Back on staff'}
                    </button>
                  </div>
                </div>

                {renaming === account.staffId && (
                  <RenamePanel
                    account={account}
                    busy={act.isPending}
                    onSave={(name) => {
                      act.mutate(() => renameStaff(account.staffId, name))
                      setRenaming(null)
                    }}
                    onSaveContact={(contact) =>
                      act.mutate(async () => {
                        const what = await setStaffContact(account.staffId, contact)

                        setDeparted(
                          what.signedOut
                            ? `${account.name} now signs in with ${contact.email}. She has been signed out everywhere.`
                            : `${account.name} is now reachable at ${contact.email ?? 'no address'}.`,
                        )
                      })
                    }
                    onCancel={() => setRenaming(null)}
                  />
                )}

                {linking === account.staffId && (
                  <FamilyLink
                    account={account}
                    busy={act.isPending}
                    onLink={(guardianId) =>
                      act.mutate(() => linkFamily(account.staffId, guardianId))
                    }
                    onUnlink={() => act.mutate(() => unlinkFamily(account.staffId))}
                  />
                )}

                {linkingDancer === account.staffId && (
                  <DancerLink
                    account={account}
                    busy={act.isPending}
                    onLink={(dancerId) =>
                      act.mutate(() => linkDancer(account.staffId, dancerId))
                    }
                    onUnlink={() => act.mutate(() => unlinkDancer(account.staffId))}
                  />
                )}

                {offerLink?.staffId === account.staffId && (
                  <ExistingLoginOffer
                    offer={offerLink}
                    busy={act.isPending}
                    onCancel={() => setOfferLink(null)}
                    onLink={() => {
                      act.mutate(() =>
                        linkExistingLogin(offerLink.staffId, offerLink.accountId),
                      )
                      setOfferLink(null)
                    }}
                  />
                )}

                {expanded === account.staffId && capabilities && (
                  <Permissions
                    account={account}
                    capabilities={capabilities}
                    busy={act.isPending}
                    onGrant={(c) => act.mutate(() => grant(account.accountId!, c))}
                    onRevoke={(c) => act.mutate(() => revoke(account.accountId!, c))}
                  />
                )}
              </li>
            ))}
          </ul>

          {/*
            <b>A footer rather than a filter</b> (D-605). Former staff are a
            pile that only grows, and the question about them is "are they out
            of my way", not "which subset am I looking at" — so it is one
            control that says how many there are and puts them back when asked,
            rather than a sixth thing to set on a screen that already has a
            search and a permissions grid.
          */}
          {former.length > 0 && (
            <button
              type="button"
              onClick={() => setShowFormer((v) => !v)}
              className="flex w-full items-center gap-2 border-t border-line px-4 py-3 text-[12.5px] font-semibold text-ink-3 hover:bg-surface-2 hover:text-ink"
            >
              {showFormer ? (
                <ChevronDown size={14} strokeWidth={2.5} />
              ) : (
                <ChevronRight size={14} strokeWidth={2.5} />
              )}
              {former.length} no longer on staff
            </button>
          )}

          {rows.length === 0 && (
            <p className="px-4 py-6 text-center text-[13px] text-ink-3">
              Nobody matches &ldquo;{query}&rdquo;.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Saying that a member of staff is also a parent here (D-305).
 *
 * **By hand, and that is the decision rather than a shortcut.** Matching on
 * email address finds fifteen of the sixteen people at this studio who are
 * both — and misses Tonya, whose staff row and parent row use different
 * addresses. A rule whose coverage nobody can trust is worse than a control
 * somebody uses sixteen times.
 *
 * So the app points and Tonya confirms. The suggestion below stays silent
 * unless the address is unique on *both* sides, because three staff share
 * `office@cabotdanceacademy.com` and a hint naming the wrong colleague gets
 * clicked.
 */
/**
 * Correcting a name (D-563).
 *
 * <b>Split into first and last rather than one box</b>, because the record is:
 * the studio matches an import on those two fields, and one free-text box
 * would have to guess where the surname starts — which is the guess that made
 * a second Ava one door over.
 *
 * <b>Shown as</b> is what every list actually reads. It is offered because the
 * bootstrap sets it (<i>Owner</i>), so correcting the name without it would
 * fix the person everywhere except the screen she is being corrected on.
 */
function RenamePanel({
  account,
  busy,
  onSave,
  onSaveContact,
  onCancel,
}: {
  account: StaffAccount
  busy: boolean
  onSave: (name: { firstName: string; lastName: string; displayName: string | null }) => void
  onSaveContact: (contact: { email: string | null; mobilePhone: string | null }) => void
  onCancel: () => void
}) {
  const [firstName, setFirstName] = useState(account.firstName ?? '')
  const [lastName, setLastName] = useState(account.lastName ?? '')
  const [displayName, setDisplayName] = useState(account.displayName ?? '')
  const [email, setEmail] = useState(account.staffEmail ?? '')
  const [phone, setPhone] = useState(account.staffPhone ?? '')

  const ready = `${firstName}${lastName}`.trim().length > 0

  const tidied = email.trim().toLowerCase() || null
  const tidiedPhone = phone.trim() || null
  const emailChanged = tidied !== (account.staffEmail ?? null)
  const contactChanged = emailChanged || tidiedPhone !== (account.staffPhone ?? null)

  /*
   * Whether her login moves with the address (D-613).
   *
   * <b>Asked of the row, never worked out here.</b> The server answers it with
   * the same function the handler uses, because this warning is a promise
   * about what that handler will do — and the first cut, comparing the two
   * addresses in the browser, disagreed with it on a difference of case.
   *
   * Clearing the address never moves a login: there is nothing to move it to,
   * so she keeps signing in as she does today.
   */
  const movesHerLogin = account.loginMoves && tidied !== null

  return (
    <div className="border-t border-line px-4 py-3.5">
      <div className="flex flex-wrap gap-2.5">
        <label className="min-w-[9rem] flex-1">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">First name</span>
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={field} />
        </label>

        <label className="min-w-[9rem] flex-1">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">Last name</span>
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={field} />
        </label>

        <label className="min-w-[9rem] flex-1">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">
            Shown as <span className="font-normal">(optional)</span>
          </span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={`${firstName} ${lastName}`.trim()}
            className={field}
          />
        </label>
      </div>

      <div className="mt-2.5 flex flex-wrap gap-2.5">
        <label className="min-w-[12rem] flex-1">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">
            Email <span className="font-normal">(where the studio writes to her)</span>
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Nothing on file"
            className={field}
          />
        </label>

        {/*
          Beside the address because the two are claimed together (D-613): a
          save marks the pair as the studio's and the next import leaves the
          pair alone, so offering only the address would freeze the number.
        */}
        <label className="min-w-[9rem] flex-1">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">
            Mobile <span className="font-normal">(optional)</span>
          </span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Nothing on file"
            className={field}
          />
        </label>
      </div>

      {/*
        Said before she presses it, never after (D-613). Changing the address
        moves her login too, which signs her out on every device - a
        consequence that lands on somebody who is not in the room.
      */}
      {emailChanged && movesHerLogin && (
        <p className="mt-2 text-[12.5px] text-warn">
          She signs in with this address. Changing it signs her out everywhere; she signs back in
          with the new address and the same password.
        </p>
      )}

      {/*
        The other half of the same question, and the one that reads as a bug if
        it goes unsaid: she has a login, and it is staying where it is.
      */}
      {emailChanged && account.accountId !== null && !movesHerLogin && (
        <p className="mt-2 text-[12.5px] text-ink-3">
          She signs in as {account.email ?? 'another address'}. That stays as it is and she is not
          signed out.
        </p>
      )}

      <p className="mt-2 text-[12px] text-ink-3">
        An import will not undo any of this &mdash; correcting an address here makes it the
        studio&rsquo;s, and later imports leave it alone.
      </p>

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          disabled={!ready || busy}
          onClick={() => {
            onSave({
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              displayName: displayName.trim() || null,
            })

            // Only when something actually moved: a save that posts an
            // unchanged address would sign her out for nothing.
            if (contactChanged) onSaveContact({ email: tidied, mobilePhone: tidiedPhone })
          }}
          className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
        >
          Save
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function FamilyLink({
  account,
  busy,
  onLink,
  onUnlink,
}: {
  account: StaffAccount
  busy: boolean
  onLink: (guardianId: string) => void
  onUnlink: () => void
}) {
  const [search, setSearch] = useState('')

  // Only once somebody types. The picker is for the case the suggestion could
  // not answer, and loading 165 households to sit unread behind a button that
  // is usually opened by mistake is a request nobody asked for.
  const { data: options, isFetching } = useQuery({
    queryKey: ['guardians', search],
    queryFn: () => searchGuardians(search),
    enabled: search.trim().length >= 2,
  })

  if (account.guardianId) {
    return (
      <div className="border-t border-line px-4 py-3.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Users size={15} strokeWidth={2} className="shrink-0 text-good" />
          <p className="text-[13.5px]">
            Also a parent here, as <strong>{account.guardianName}</strong>.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={onUnlink}
            className="ml-auto rounded-lg border border-line-2 px-2.5 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:border-crit hover:text-crit disabled:opacity-50"
          >
            Not the same person
          </button>
        </div>
        <p className="mt-1.5 text-[12.5px] text-ink-3">
          She sees her own children in the family view, and her own classes in the teacher
          view. Removing this signs her out of her current session.
        </p>
      </div>
    )
  }

  return (
    <div className="border-t border-line px-4 py-3.5">
      <p className="text-[13.5px] font-semibold">Is {account.name} also a parent here?</p>
      <p className="mt-0.5 text-[12.5px] text-ink-3">
        If her own children dance here, say so and she will see both.
      </p>

      {account.suggestedGuardianId && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-warn bg-warn-bg px-3 py-2.5">
          <p className="text-[13px]">
            The same email address is on <strong>{account.suggestedGuardianName}</strong>.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => onLink(account.suggestedGuardianId!)}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-accent px-2.5 py-1.5 text-[12.5px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-50"
          >
            <Check size={13} strokeWidth={2.5} />
            Yes, that is her
          </button>
        </div>
      )}

      <div className="mt-2.5 flex items-center gap-2 rounded-xl border border-line-2 bg-surface px-3 py-2">
        <Search size={14} strokeWidth={2} className="shrink-0 text-ink-3" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search parents by name or email"
          // py-1.5 rather than none: the bare input measured 20px tall, under
          // the 24px tap minimum the audit checks for. Found by rendering it.
          className="min-w-0 flex-1 bg-transparent py-1.5 text-[13px] outline-none"
        />
      </div>

      {search.trim().length >= 2 && (
        <ul className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-line">
          {options?.map((option) => (
            <li key={option.id} className="border-b border-line last:border-b-0">
              <button
                type="button"
                disabled={busy || option.alreadyLinked}
                onClick={() => onLink(option.id)}
                className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 px-3 py-2 text-left hover:bg-surface-2 disabled:opacity-45"
              >
                <span className="text-[13px] font-semibold">{option.name}</span>
                <span className="text-[12px] text-ink-3">{option.household}</span>
                {option.email && (
                  <span className="wrap-anywhere text-[12px] text-ink-3">{option.email}</span>
                )}
                {option.alreadyLinked && (
                  <span className="ml-auto text-[12px] text-ink-3">Already linked</span>
                )}
              </button>
            </li>
          ))}

          {!isFetching && options?.length === 0 && (
            <li className="px-3 py-3 text-center text-[12.5px] text-ink-3">
              No parent matches that.
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

/**
 * Saying that a member of staff is also a dancer here (D-604).
 *
 * <b>The same picker as the family one, and deliberately not the same
 * mechanism.</b> A family link lives on the account, because it decides what
 * that login may see about itself. This one is person to person on the staff
 * record: 43 of the 48 staff have no login, so it is offered on every row.
 *
 * <b>It points and never acts.</b> A name match is a hint ordered first, and
 * the write takes an explicit id — matching on a name is what made a second
 * Ava out of a changed surname (D-535).
 */
function DancerLink({
  account,
  busy,
  onLink,
  onUnlink,
}: {
  account: StaffAccount
  busy: boolean
  onLink: (dancerId: string) => void
  onUnlink: () => void
}) {
  const [search, setSearch] = useState('')

  const { data: options, isPending } = useQuery({
    queryKey: ['dancer-candidates', account.staffId, search],
    queryFn: () => dancerCandidates(account.staffId, search),
    enabled: !account.dancerId,
  })

  if (account.dancerId) {
    return (
      <div className="border-t border-line px-4 py-3.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Sparkles size={15} strokeWidth={2} className="shrink-0 text-good" />
          <p className="text-[13.5px]">
            Also a dancer here, as <strong>{account.dancerName}</strong>.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={onUnlink}
            className="ml-auto rounded-lg border border-line-2 px-2.5 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:border-crit hover:text-crit disabled:opacity-50"
          >
            Not the same person
          </button>
        </div>
        <p className="mt-1.5 text-[12.5px] text-ink-3">
          If she signs in, she sees her own classes as a dancer as well as the ones she helps
          teach.
        </p>
      </div>
    )
  }

  const shown: DancerOption[] = options ?? []

  return (
    <div className="border-t border-line px-4 py-3.5">
      <p className="text-[13.5px] font-semibold">Is {account.name} also a dancer here?</p>
      <p className="mt-0.5 text-[12.5px] text-ink-3">
        Student teachers are on both lists. Saying so keeps them one person rather than two.
      </p>

      <div className="mt-2.5 flex items-center gap-2 rounded-xl border border-line-2 bg-surface px-3 py-2">
        <Search size={14} strokeWidth={2} className="shrink-0 text-ink-3" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search dancers by name"
          aria-label="Search dancers by name"
          className="min-w-0 flex-1 bg-transparent py-1.5 text-[13px] outline-none"
        />
      </div>

      <ul className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-line">
        {shown.map((option) => (
          <li key={option.id} className="border-b border-line last:border-b-0">
            <button
              type="button"
              disabled={busy}
              onClick={() => onLink(option.id)}
              className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 px-3 py-2 text-left hover:bg-surface-2 disabled:opacity-45"
            >
              <span className="text-[13px] font-semibold">{option.name}</span>
              {option.email && (
                <span className="wrap-anywhere text-[12px] text-ink-3">{option.email}</span>
              )}
              {option.likelyMatch && (
                <span className="ml-auto text-[12px] font-semibold text-warn">Same name</span>
              )}
            </button>
          </li>
        ))}

        {isPending && (
          <li className="px-3 py-3 text-center text-[12.5px] text-ink-3">Loading...</li>
        )}

        {!isPending && shown.length === 0 && (
          <li className="px-3 py-3 text-center text-[12.5px] text-ink-3">
            No dancer matches that.
          </li>
        )}
      </ul>
    </div>
  )
}

/**
 * She already signs in, so there is nothing to invite (D-604).
 *
 * <b>The bug this replaces.</b> A parent who made her own account, then had a
 * staff record invited, got *"That address already has an active login. Use a
 * password reset instead"* and no way forward — a reset would not have made
 * her a teacher either.
 *
 * <b>Offered, never taken.</b> Linking hands teaching capabilities to an
 * account somebody else created, so the app names the login and Tonya says
 * they are one person. Doing it on the email address alone is the
 * account-takeover path D-535 refused.
 */
function ExistingLoginOffer({
  offer,
  busy,
  onLink,
  onCancel,
}: {
  offer: { staffName: string; accountName: string }
  busy: boolean
  onLink: () => void
  onCancel: () => void
}) {
  return (
    <div className="border-t border-line bg-warn-bg px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Link2 size={15} strokeWidth={2} className="shrink-0 text-warn" />
        <p className="min-w-0 flex-1 text-[13.5px]">
          That address already signs in as <strong>{offer.accountName}</strong>. Attach this
          staff record to that login instead of inviting her again.
        </p>
      </div>

      <p className="mt-1.5 text-[12.5px] text-ink-3">
        She keeps the password she chose and nothing is emailed. That login can then take
        attendance and see her own schedule, so only do this if {offer.staffName} and{' '}
        {offer.accountName} are the same person.
      </p>

      <div className="mt-2.5 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onLink}
          className="flex items-center gap-1.5 rounded-lg bg-accent px-2.5 py-1.5 text-[12.5px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-50"
        >
          <Check size={13} strokeWidth={2.5} />
          Yes, same person
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-line-2 px-2.5 py-1.5 text-[12.5px] font-semibold text-ink-3"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function RoleControl({
  account,
  onChange,
  disabled,
}: {
  account: StaffAccount
  onChange: (role: UserRole) => void
  disabled: boolean
}) {
  return (
    <select
      value={account.role ?? 'Teacher'}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as UserRole)}
      aria-label={`Role for ${account.name}`}
      className="w-[6.5rem] shrink-0 rounded-lg border border-line-2 bg-surface-2 px-2 py-1.5 text-[12.5px] font-semibold disabled:opacity-50"
    >
      {ROLES.map((role) => (
        <option key={role} value={role}>
          {role}
        </option>
      ))}
    </select>
  )
}

/**
 * Extra permissions on top of the role (D-5).
 *
 * Shows every capability, not only the grantable ones, because the useful
 * question is "what can this person do?" — and an answer that omitted
 * everything their role already covers would read as a much shorter list than
 * the truth. Role-derived ones are shown as held but not removable: revoking
 * something the role grants would be a button that does nothing.
 */
function Permissions({
  account,
  capabilities,
  busy,
  onGrant,
  onRevoke,
}: {
  account: StaffAccount
  capabilities: CapabilityInfo[]
  busy: boolean
  onGrant: (capability: string) => void
  onRevoke: (capability: string) => void
}) {
  const granted = new Set(account.grantedCapabilities)
  const effective = new Set(account.effectiveCapabilities)

  /*
   * ⚠ **Which permission is waiting on a second tap** (D-919).
   *
   * The chip is a toggle and the two directions are not the same act: granting
   * adds something and is safe to do by accident, revoking takes it away and
   * is not. It had no confirmation either way, and `send()` throws the
   * response body away — so an owner who pressed again because nothing
   * appeared to happen **removed** the permission she had just given, and the
   * screen agreed with her. Reported as a grant that did not take.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  return (
    <div className="border-t border-line bg-surface-2 px-4 py-3.5">
      <p className="mb-3 text-[12.5px] text-ink-3">
        Ticked permissions this person holds. Those from their <strong>{account.role}</strong> role
        cannot be removed here &mdash; change the role instead.
      </p>

      {confirming !== null && (
        <ConfirmAction
          title="Remove this permission?"
          body={`${humanise(confirming)} goes back to whatever their ${account.role} role gives them.`}
          confirmLabel="Remove it"
          busyLabel="Removing..."
          busy={busy}
          onConfirm={() => {
            onRevoke(confirming)
            setConfirming(null)
          }}
          onDismiss={() => setConfirming(null)}
        />
      )}

      <div className="flex flex-col gap-3">
        {byGroup(capabilities).map(({ group, items }) => (
          <div key={group}>
            <p className="mb-1.5 text-[11px] font-bold tracking-[0.1em] text-ink-3 uppercase">
              {group}
            </p>

            <div className="flex flex-wrap gap-1.5">
              {items.map((capability) => {
                const isGranted = granted.has(capability.name)
                const isHeld = effective.has(capability.name)
                const fromRole = isHeld && !isGranted

                return (
                  <button
                    key={capability.name}
                    type="button"
                    disabled={busy || fromRole}
                    title={
                      fromRole
                        ? `Comes with the ${account.role} role`
                        : isGranted
                          ? 'Granted individually — click to remove'
                          : 'Click to grant'
                    }
                    // Granting is one tap; removing asks first (D-919).
                    onClick={() =>
                      isGranted ? setConfirming(capability.name) : onGrant(capability.name)
                    }
                    className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[12px] font-semibold transition-colors disabled:cursor-default ${
                      isGranted
                        ? 'border-accent bg-accent-soft text-accent-2'
                        : fromRole
                          ? 'border-line bg-surface text-ink-3'
                          : 'border-line-2 bg-surface text-ink-3 hover:border-ink-3 hover:text-ink'
                    }`}
                  >
                    {isHeld && <Check size={11} strokeWidth={3} className="shrink-0" />}
                    {humanise(capability.name)}
                    {capability.ownerOnlyByDefault && !isHeld && (
                      <ShieldCheck
                        size={11}
                        strokeWidth={2.5}
                        className="shrink-0 text-warn"
                        aria-label="Owner-only by default"
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 flex items-center gap-1.5 text-[11.5px] text-ink-3">
        <ShieldCheck size={12} strokeWidth={2.5} className="shrink-0 text-warn" />
        Marked permissions are owner-only by default. Granting one hands over something the
        role deliberately withholds.
      </p>
    </div>
  )
}

/**
 * Adding somebody the import has not brought in (D-534).
 *
 * **A person, not a login.** Nothing here invites her; 43 of the 48 staff have
 * no account and that is the ordinary state. The form asks for an address
 * because a row without one cannot be recognised by a later Jackrabbit import
 * and comes back as a second person (D-93).
 */
function AddTeacher({
  onClose,
  onAdded,
}: {
  onClose: () => void
  onAdded: (name: string, staffId: string) => void
}) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const add = useMutation({
    mutationFn: () =>
      addStaff({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || null,
        mobilePhone: phone.trim() || null,
      }),
    onSuccess: (staff) => onAdded(staff.name, staff.id),
  })

  /*
   * A name and an address. The name half mirrors the server exactly (either
   * will do); the address is required because a row without one cannot be
   * adopted by a later import and comes back as a second person.
   */
  const ready =
    (firstName.trim().length > 0 || lastName.trim().length > 0) && email.trim().length > 0

  return (
    <form
      className="mb-4 rounded-2xl border border-line bg-surface p-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (ready && !add.isPending) add.mutate()
      }}
    >
      <h2 className="text-[14px] font-semibold">Add someone to the staff list</h2>

      <p className="mt-0.5 mb-3 text-[12.5px] text-ink-3">
        This creates their record. It does not give them a login &mdash; invite them
        separately once they are here.
      </p>

      <div className="grid gap-2.5 sm:grid-cols-2">
        <label className="text-[12.5px] font-semibold">
          First name
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-[13.5px] font-normal"
          />
        </label>

        <label className="text-[12.5px] font-semibold">
          Last name
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-[13.5px] font-normal"
          />
        </label>

        <label className="text-[12.5px] font-semibold">
          Email
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-[13.5px] font-normal"
          />
          {/*
            **Required, and the caption says why rather than just marking it.**
            An earlier version called it "worth filling in", which is a
            suggestion — and skipping it produces a row no Jackrabbit import can
            recognise, so the next export quietly creates a second person
            holding the key and all her classes.
          */}
          <span className="mt-1 block font-normal text-[11.5px] text-ink-3">
            Needed &mdash; it is how a Jackrabbit import recognises her as the same
            person rather than adding her twice.
          </span>
        </label>

        <label className="text-[12.5px] font-semibold">
          Mobile
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-[13.5px] font-normal"
          />
        </label>
      </div>

      {add.error && (
        <p role="alert" className="mt-2.5 text-[13px] text-crit">
          {(add.error as Error).message}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={!ready || add.isPending}
          className="rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent disabled:opacity-60"
        >
          {add.isPending ? 'Adding...' : 'Add to the list'}
        </button>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-line px-3.5 py-2 text-[13px] font-semibold"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
